import React, { useState, useEffect } from 'react';
import { Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import * as pdfjsLib from 'pdfjs-dist';
import { Button, Modal, Spinner, Sym } from '../../../components/app2';
import AlertService from '../../../utils/alertService';
import { strapiService } from '../../../utils/api';

// Import the styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

/**
 * Visor del recibo (rediseño jul-2026).
 *
 * El diseño propone una "vista previa ligera" dibujada a mano; aquí se conserva
 * el VISOR REAL de pdf.js (@react-pdf-viewer) con miniaturas, navegación de
 * páginas, zoom y búsqueda, porque cambiarlo por una maqueta sería perder
 * funcionalidad. Lo que se rediseña es el marco: el modal común del sistema
 * (Escape, foco, bloqueo de scroll) y la barra de descargas PDF / XML / Ambos.
 */

// Configure PDF.js worker with enhanced error handling and security
const configurePDFWorker = () => {
  try {
    if (typeof window !== 'undefined' && !window.pdfjsWorkerConfigured) {
      // Primary: Use bundled worker with Vite URL handling
      const workerUrl = new URL(
        'pdfjs-dist/build/pdf.worker.min.js',
        import.meta.url
      )?.toString();

      pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
      window.pdfjsWorkerConfigured = true;

      console.log('PDF.js worker configured successfully:', workerUrl);
    }
  } catch (error) {
    console.warn('Local PDF.js worker configuration failed, using CDN fallback:', error);

    // Fallback: Use CDN worker that matches our exact version
    try {
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.0.279/pdf.worker.min.js';
      window.pdfjsWorkerConfigured = true;
    } catch (fallbackError) {
      console.error('CDN fallback also failed:', fallbackError);
      throw new Error('Unable to configure PDF.js worker');
    }
  }
};

/** Contenedor centrado para los estados del visor (cargando / error). */
const ViewerState = ({ children }) => (
  <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 24 }}>
    <div style={{ textAlign: 'center', maxWidth: 420 }}>{children}</div>
  </div>
);

const PDFPreviewModal = ({ isOpen, onClose, receipt }) => {
  const [downloadLoading, setDownloadLoading] = useState({
    pdf: false,
    xml: false,
    both: false
  });
  const [pdfError, setPdfError] = useState(null);
  const [isWorkerReady, setIsWorkerReady] = useState(false);

  // Configure PDF worker on component mount with error handling
  useEffect(() => {
    const initializeWorker = async () => {
      try {
        configurePDFWorker();
        setIsWorkerReady(true);
        setPdfError(null);
      } catch (error) {
        console.error('Failed to initialize PDF worker:', error);
        setPdfError('Error al inicializar el visor de PDF');
        setIsWorkerReady(false);
      }
    };

    if (isOpen) {
      initializeWorker();
    }
  }, [isOpen]);

  // Create plugin instance with optimized configuration
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [
      defaultTabs?.[0], // Thumbnails tab
      defaultTabs?.[1], // Bookmarks tab
    ],
    toolbar: {
      searchPlugin: {
        keyword: '',
      },
      moreActionsPopover: {
        downloadMenuItem: {
          label: 'Descargar PDF'
        }
      }
    },
  });

  // Enhanced PDF URL generation with proper error handling
  const getPDFUrl = (r) => {
    try {
      // Use the pdfUrl from the receipt object (blob URL created from API response)
      if (r?.pdfUrl) {
        return r?.pdfUrl;
      }

      console.warn('No PDF URL available in receipt object');
      setPdfError('No se pudo obtener la URL del PDF');
      return null;
    } catch (error) {
      console.error('Error generating PDF URL:', error);
      setPdfError('No se pudo generar la URL del PDF');
      return null;
    }
  };

  // Helper function to download blob as file
  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async (type) => {
    setDownloadLoading(prev => ({ ...prev, [type]: true }));

    try {
      switch (type) {
        case 'pdf': {
          // Download PDF from API
          const pdfBlob = await strapiService?.payroll?.downloadPDF(receipt?.id);
          downloadFile(pdfBlob, `recibo-${receipt?.folio || receipt?.uuid}.pdf`);

          await AlertService?.success(
            'PDF descargado',
            'El archivo PDF se ha descargado correctamente.'
          );
          break;
        }

        case 'xml': {
          // Download XML from API
          const xmlBlob = await strapiService?.payroll?.downloadXML(receipt?.id);
          downloadFile(xmlBlob, `recibo-${receipt?.folio || receipt?.uuid}.xml`);

          await AlertService?.success(
            'XML descargado',
            'El archivo XML se ha descargado correctamente.'
          );
          break;
        }

        case 'both': {
          // Download both PDF and XML
          const [pdfBlob, xmlBlob] = await Promise.all([
            strapiService?.payroll?.downloadPDF(receipt?.id),
            strapiService?.payroll?.downloadXML(receipt?.id)
          ]);

          downloadFile(pdfBlob, `recibo-${receipt?.folio || receipt?.uuid}.pdf`);
          downloadFile(xmlBlob, `recibo-${receipt?.folio || receipt?.uuid}.xml`);

          await AlertService?.success(
            'Archivos descargados',
            'Los archivos PDF y XML se han descargado correctamente.'
          );
          break;
        }

        default:
          throw new Error('Tipo de descarga no válido');
      }

    } catch (error) {
      console.error('Download error:', error);

      // Handle specific errors
      if (error?.response?.status === 404) {
        await AlertService?.error(
          'Recibo no encontrado',
          'El recibo de nómina no existe o fue eliminado.'
        );
      } else if (error?.response?.status === 403) {
        await AlertService?.error(
          'Sin permisos',
          'No tiene permisos para descargar este recibo.'
        );
      } else {
        await AlertService?.error(
          'Error al descargar',
          'No se pudo descargar el archivo. Por favor intente nuevamente.'
        );
      }
    } finally {
      setDownloadLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // Handle PDF viewer errors
  const handleDocumentLoadError = (error) => {
    console.error('PDF document load error:', error);
    setPdfError('No se pudo cargar el documento PDF. Verifica que el archivo sea válido.');
  };

  if (!isOpen) return null;

  const pdfUrl = getPDFUrl(receipt);
  const anyDownloading = Object.values(downloadLoading).some(Boolean);

  const footer = (
    <>
      <span
        className="a2-hide-sm-flex"
        style={{
          alignItems: 'center', gap: 7, marginRight: 'auto',
          font: "500 .8rem 'Hanken Grotesk'", color: 'var(--muted)',
        }}
      >
        <Sym name="info" size={16} />
        Usa los controles del visor para navegar y hacer zoom
      </span>

      <Button
        variant="outline"
        size="sm"
        icon="picture_as_pdf"
        onClick={() => handleDownload('pdf')}
        disabled={anyDownloading || !!pdfError}
        loading={downloadLoading?.pdf}
      >
        PDF
      </Button>

      <Button
        variant="outline"
        size="sm"
        icon="code"
        onClick={() => handleDownload('xml')}
        disabled={anyDownloading || !!pdfError}
        loading={downloadLoading?.xml}
      >
        XML
      </Button>

      <Button
        variant="primary"
        size="sm"
        icon="download"
        onClick={() => handleDownload('both')}
        disabled={anyDownloading || !!pdfError}
        loading={downloadLoading?.both}
      >
        Ambos
      </Button>
    </>
  );

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      maxWidth={1100}
      bodyFlush
      title={`Recibo · ${receipt?.payPeriod || 'Documento'}`}
      subtitle={receipt?.folio ? `Folio ${receipt.folio}` : receipt?.uuid ? `CFDI ${receipt.uuid}` : undefined}
      footer={footer}
    >
      {/* El documento siempre se muestra sobre blanco: es una hoja, no una
          superficie de la interfaz, así que no sigue el tema claro/oscuro. */}
      <div style={{ height: 'min(72vh, 860px)', background: '#fff', overflow: 'hidden' }}>
        {pdfError ? (
          <ViewerState>
            <Sym name="error" size={46} style={{ color: 'var(--danger)' }} />
            <p style={{ font: "600 1rem 'Hanken Grotesk'", color: 'var(--heading)', margin: '12px 0 4px' }}>
              Error al cargar el PDF
            </p>
            <p style={{ font: "500 .88rem 'Hanken Grotesk'", color: 'var(--muted)', margin: '0 0 16px' }}>
              {pdfError}
            </p>
            <Button
              variant="outline"
              size="sm"
              icon="refresh"
              onClick={() => {
                setPdfError(null);
                setIsWorkerReady(false);
                configurePDFWorker();
                setIsWorkerReady(true);
              }}
            >
              Reintentar
            </Button>
          </ViewerState>
        ) : !isWorkerReady ? (
          <ViewerState>
            <Spinner size={30} />
            <p style={{ font: "500 .9rem 'Hanken Grotesk'", color: 'var(--muted)', marginTop: 14 }}>
              Inicializando visor de PDF…
            </p>
          </ViewerState>
        ) : pdfUrl ? (
          <Viewer
            fileUrl={pdfUrl}
            plugins={[defaultLayoutPluginInstance]}
            theme="light"
            onDocumentLoadSuccess={() => {
              setPdfError(null);
              console.log('PDF loaded successfully');
            }}
            onDocumentLoadError={handleDocumentLoadError}
            renderError={(error) => (
              <ViewerState>
                <Sym name="broken_image" size={46} style={{ color: 'var(--danger)' }} />
                <p style={{ font: "600 1rem 'Hanken Grotesk'", color: 'var(--heading)', margin: '12px 0 4px' }}>
                  No se pudo mostrar el PDF
                </p>
                <p style={{ font: "500 .88rem 'Hanken Grotesk'", color: 'var(--muted)', margin: 0 }}>
                  {error?.message || 'Error desconocido'}
                </p>
              </ViewerState>
            )}
          />
        ) : (
          <ViewerState>
            <Spinner size={30} />
            <p style={{ font: "500 .9rem 'Hanken Grotesk'", color: 'var(--muted)', marginTop: 14 }}>
              Cargando documento…
            </p>
          </ViewerState>
        )}
      </div>
    </Modal>
  );
};

export default PDFPreviewModal;
